import { ORDER_STATUS_MAPPING } from '@constants/order';
import { type Order, OrderStatus } from '@customTypes/order';
import type { Requisition } from '@customTypes/requisition';
import { isOrderReported } from '@helpers/order';
import { getRequisitionStatusKey } from '@helpers/requisition';

/** Stages Home can show truthfully from patient-portal requisition data, in order. */
export const HOME_TIMELINE_STAGES = [
  'order-placed',
  'order-approved',
  'awaiting-sample',
  'analysis',
  'clinician-review',
  'results-ready',
] as const;

export type HomeTimelineStageId = (typeof HOME_TIMELINE_STAGES)[number];

export type HomeTimelineStepStatus = 'complete' | 'current' | 'upcoming';

/** Labels are the verbatim strings already shipped in `ORDER_STATUS_STEPS`. */
const STAGE_LABELS: Record<HomeTimelineStageId, string> = {
  'order-placed': 'Order Placed',
  'order-approved': 'Order Approved',
  'awaiting-sample': 'Awaiting Sample',
  analysis: 'Analysis',
  'clinician-review': 'Clinician Review',
  'results-ready': 'Results Ready',
};

// Approved is its own step to match the order-detail stepper. It carries no date
// because the payload has no `approved_at`; the status alone places the playhead.
const STATUS_STAGES: Partial<Record<OrderStatus, HomeTimelineStageId>> = {
  [OrderStatus.SUBMITTED]: 'order-placed',
  [OrderStatus.APPROVED]: 'order-approved',
  [OrderStatus.AWAITING_SAMPLE]: 'awaiting-sample',
  [OrderStatus.ANALYZING]: 'analysis',
  [OrderStatus.REFERRAL_LAB_LD_REVIEW]: 'analysis',
  [OrderStatus.CLINICIAN_REVIEW]: 'clinician-review',
  [OrderStatus.COMPLETED]: 'results-ready',
  [OrderStatus.REPORTED]: 'results-ready',
};

export const HOME_TERMINAL_STATUSES = [OrderStatus.CANCELED, OrderStatus.DENIED, OrderStatus.FAILED] as const;

export type HomeTerminalStatus = (typeof HOME_TERMINAL_STATUSES)[number];

export const isHomeTerminalStatus = (status?: OrderStatus | null): status is HomeTerminalStatus =>
  !!status && (HOME_TERMINAL_STATUSES as readonly OrderStatus[]).includes(status);

export const getHomeTimelineStage = (status?: OrderStatus | null): HomeTimelineStageId | null =>
  status ? (STATUS_STAGES[status] ?? null) : null;

/** Newest `reported_at` across a requisition's orders; undefined when nothing is reported. */
export const getLatestReportedAt = (orders: Order[] = []): string | undefined => {
  let latest: string | undefined;
  let latestTime = -Infinity;

  orders.forEach(order => {
    if (!order.reported_at) return;
    const time = Date.parse(order.reported_at);
    if (Number.isNaN(time) || time <= latestTime) return;
    latest = order.reported_at;
    latestTime = time;
  });

  return latest;
};

export interface HomeOrderProgress {
  total: number;
  reported: number;
  isFullyReported: boolean;
  /** Some products in the requisition are reported while others are not. */
  isPartiallyReported: boolean;
}

export const getHomeOrderProgress = (requisition: Requisition): HomeOrderProgress => {
  const orders = requisition.orders ?? [];
  const reported = orders.filter(isOrderReported).length;

  return {
    total: orders.length,
    reported,
    isFullyReported: orders.length > 0 && reported === orders.length,
    isPartiallyReported: reported > 0 && reported < orders.length,
  };
};

export interface HomeTimelineStep {
  id: HomeTimelineStageId;
  label: string;
  status: HomeTimelineStepStatus;
  /** Raw API timestamp; omitted when no real date exists for this stage. */
  date?: string;
}

export type HomeOrderTimeline =
  | { kind: 'progress'; currentStage: HomeTimelineStageId; steps: HomeTimelineStep[] }
  | { kind: 'terminal'; status: HomeTerminalStatus; label: string };

export type HomeOrderTimelineProgress = Extract<HomeOrderTimeline, { kind: 'progress' }>;

interface StageDate {
  date?: string;
}

const getStageDate = (stage: HomeTimelineStageId, requisition: Requisition, reportedAt?: string): StageDate => {
  if (stage === 'order-placed') {
    return requisition.submitted_at ? { date: requisition.submitted_at } : {};
  }
  // Sample arrival is what completes this stage, so it reads as the stage's own date.
  if (stage === 'awaiting-sample' && requisition.sample_received_at) {
    return { date: requisition.sample_received_at };
  }
  if (stage === 'results-ready' && reportedAt) {
    return { date: reportedAt };
  }
  return {};
};

/**
 * Returns null when the requisition has no orders, so callers can omit the timeline
 * instead of rendering an empty rail.
 */
export const buildHomeOrderTimeline = (requisition: Requisition): HomeOrderTimeline | null => {
  const status = getRequisitionStatusKey(requisition);

  if (isHomeTerminalStatus(status)) {
    return { kind: 'terminal', status, label: ORDER_STATUS_MAPPING[status].label };
  }

  const currentStage = getHomeTimelineStage(status);
  if (!currentStage) return null;

  const currentIndex = HOME_TIMELINE_STAGES.indexOf(currentStage);
  const reportedAt = getLatestReportedAt(requisition.orders);

  const steps = HOME_TIMELINE_STAGES.map((stage, index) => {
    const stepStatus: HomeTimelineStepStatus =
      index < currentIndex ? 'complete' : index === currentIndex ? 'current' : 'upcoming';

    // Dates on an upcoming stage would contradict the playhead, so only reached stages carry one.
    const stageDate = stepStatus === 'upcoming' ? {} : getStageDate(stage, requisition, reportedAt);

    return { id: stage, label: STAGE_LABELS[stage], status: stepStatus, ...stageDate };
  });

  return { kind: 'progress', currentStage, steps };
};
